import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslatedLegacySettings } from '@/hooks/useTranslatedLegacySettings';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface UnifiedStatusSelectorProps {
    value: string;
    statusType: 'lead' | 'project' | 'workOrder';
    mode: 'editable' | 'form' | 'readonly';
    updateRoute?: string;
    resourceId?: number | string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    showTerminalWarning?: boolean;
}

// Modal content component that can use the useModal hook
function StatusModalContent({
    statusType,
    selectedValue,
    setSelectedValue,
    lostReason,
    setLostReason,
}: {
    statusType: 'lead' | 'project' | 'workOrder';
    selectedValue: string;
    setSelectedValue: (value: string) => void;
    showTerminalWarning?: boolean;
    lostReason?: string;
    setLostReason?: (value: string) => void;
}) {
    const { legacy } = useTranslatedLegacySettings();
    const { t } = useTranslations();

    // Map statusType to correct settings property
    const getStatusOptions = (): Array<{ value: string; label: string; color: string; isTerminal?: boolean }> => {
        if (!legacy) return [];

        switch (statusType) {
            case 'lead':
                return legacy.LEAD_STATUS_OPTIONS || [];
            case 'project':
                return legacy.PROJECT_STATUS_OPTIONS || [];
            case 'workOrder':
                return legacy.WORK_ORDER_STATUS_OPTIONS || [];
            default:
                return [];
        }
    };

    const statusOptions = getStatusOptions();

    const handleValueChange = (newValue: string) => {
        setSelectedValue(newValue);
    };

    const getStatusOption = (status: string) => {
        return statusOptions.find((option: { value: string; label: string; color: string; isTerminal?: boolean }) => option.value === status);
    };

    const getStatusBadgeClasses = (status: string) => {
        const option = getStatusOption(status);
        // logger.log('Modal Status:', status, 'Option:', option); // Debug log
        if (!option) return 'bg-muted text-foreground border-border';
        return option.color;
    };

    return (
        <div className="space-y-6">
            <RadioGroup value={selectedValue} onValueChange={handleValueChange} className="space-y-3">
                {statusOptions.map((option: { value: string; label: string; color: string; isTerminal?: boolean }) => (
                    <div
                        key={option.value}
                        className="border-border hover:bg-accent flex items-center justify-between rounded-lg border p-3 transition-colors"
                    >
                        <Label htmlFor={option.value} className="flex flex-1 cursor-pointer items-center space-x-2">
                            <span
                                className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium ${getStatusBadgeClasses(option.value)}`}
                            >
                                {option.label}
                            </span>
                        </Label>
                        <RadioGroupItem value={option.value} id={option.value} />
                    </div>
                ))}
            </RadioGroup>

            {/* Show lost_reason textarea when status is "lost" for leads */}
            {statusType === 'lead' && selectedValue === 'lost' && (
                <div className="space-y-2 border-t pt-4">
                    <Label htmlFor="lost_reason" className="text-sm font-medium">
                        {t('leads.forms.lost_reason.label', 'Reason for Loss')} *
                    </Label>
                    <Textarea
                        id="lost_reason"
                        value={lostReason || ''}
                        onChange={(e) => setLostReason?.(e.target.value)}
                        placeholder={t('leads.forms.lost_reason.placeholder', 'Please explain why this lead was lost...')}
                        className="min-h-[100px]"
                        required
                    />
                    <p className="text-muted-foreground text-xs">
                        {t('leads.forms.lost_reason.help', 'This field is required when marking a lead as lost.')}
                    </p>
                </div>
            )}
        </div>
    );
}

// Modal footer component
function StatusModalFooter({
    value,
    mode,
    updateRoute,
    resourceId,
    onChange,
    selectedValue,
    setSelectedValue,
    onClose,
    statusType,
    lostReason,
}: Omit<UnifiedStatusSelectorProps, 'placeholder' | 'className' | 'disabled'> & {
    selectedValue: string;
    setSelectedValue: (value: string) => void;
    onClose: () => void;
    lostReason?: string;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslations();

    const handleSave = async () => {
        if (mode === 'form' && onChange) {
            onChange(selectedValue);
            onClose();
            return;
        }

        // Validate lost_reason if status is "lost" for leads
        if (statusType === 'lead' && selectedValue === 'lost' && (!lostReason || lostReason.trim() === '')) {
            toast.error(t('leads.forms.lost_reason.required', 'Please provide a reason for why this lead was lost.'));
            return;
        }

        if (mode === 'editable' && updateRoute && resourceId) {
            setIsLoading(true);

            try {
                const data: { status: string; lost_reason?: string } = {
                    status: selectedValue,
                };

                // Include lost_reason if status is "lost" for leads
                if (statusType === 'lead' && selectedValue === 'lost') {
                    data.lost_reason = lostReason || '';
                }

                router.patch(route(updateRoute, resourceId), data, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        onClose();
                    },
                    onError: (errors) => {
                        console.error('Status update failed:', errors);
                        toast.error(t('common.failed_to_update_status', 'Failed to update status'));
                        setSelectedValue(value); // Revert on error
                    },
                    onFinish: () => {
                        setIsLoading(false);
                    },
                });
            } catch (error) {
                console.error('Status update failed:', error);
                toast.error(t('common.failed_to_update_status', 'Failed to update status'));
                setSelectedValue(value); // Revert on error
                setIsLoading(false);
            }
        }
    };

    const handleCancel = () => {
        setSelectedValue(value); // Revert to original value
        onClose();
    };

    return (
        <>
            <Button variant="outline" onClick={handleCancel} disabled={isLoading} className="w-full md:w-auto">
                {t('common.cancel', 'Cancel')}
            </Button>
            <Button
                className="w-full md:w-auto"
                onClick={handleSave}
                disabled={
                    isLoading ||
                    selectedValue === value ||
                    (statusType === 'lead' && selectedValue === 'lost' && (!lostReason || lostReason.trim() === ''))
                }
                variant="default"
            >
                {isLoading ? t('common.saving', 'Saving...') : t('common.save_status', 'Save Status')}
            </Button>
        </>
    );
}

// Main component
export function UnifiedStatusSelector({
    value,
    statusType,
    mode,
    updateRoute,
    resourceId,
    onChange,
    placeholder,
    className,
    disabled = false,
    showTerminalWarning = true,
}: UnifiedStatusSelectorProps) {
    const { t } = useTranslations();
    const [selectedValue, setSelectedValue] = useState(value);
    const [lostReason, setLostReason] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const { legacy } = useTranslatedLegacySettings();
    const defaultPlaceholder = placeholder || t('common.select_status', 'Select status');

    // Sync selectedValue with value prop
    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    // Map statusType to correct settings property
    const getStatusOptions = (): Array<{ value: string; label: string; color: string; isTerminal?: boolean }> => {
        if (!legacy) return [];

        switch (statusType) {
            case 'lead':
                return legacy.LEAD_STATUS_OPTIONS || [];
            case 'project':
                return legacy.PROJECT_STATUS_OPTIONS || [];
            case 'workOrder':
                return legacy.WORK_ORDER_STATUS_OPTIONS || [];
            default:
                return [];
        }
    };

    const statusOptions = getStatusOptions();
    const currentOption = statusOptions.find(
        (option: { value: string; label: string; color: string; isTerminal?: boolean }) => option.value === value,
    );
    const displayValue = currentOption?.label || value || defaultPlaceholder;

    const getStatusOption = (status: string) => {
        return statusOptions.find((option: { value: string; label: string; color: string; isTerminal?: boolean }) => option.value === status);
    };

    const getStatusBadgeClasses = (status: string) => {
        const option = getStatusOption(status);
        if (!option) return 'bg-muted text-foreground border-border';
        return option.color;
    };

    if (mode === 'readonly') {
        return (
            <span className={`inline-flex h-auto items-center p-1 ${className ?? ''} ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
                <span
                    className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium ${getStatusBadgeClasses(value)}`}
                >
                    <span>{displayValue}</span>
                </span>
            </span>
        );
    }

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
            <ResponsiveModalTrigger asChild>
                <Button
                    variant="ghost"
                    className={`h-auto p-1 ${className} ${disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : ''}`}
                    disabled={disabled}
                >
                    <span
                        className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium ${getStatusBadgeClasses(value)}`}
                    >
                        <span>{displayValue}</span>
                    </span>
                </Button>
            </ResponsiveModalTrigger>
            <ResponsiveModalContent drawerClassName="data-[vaul-drawer-direction=bottom]:max-h-[85dvh]">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('common.select_status', 'Select Status')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>{t('common.choose_status', 'Choose the status for this item')}</ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <ResponsiveModalBody>
                    <StatusModalContent
                        statusType={statusType}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        showTerminalWarning={showTerminalWarning}
                        lostReason={lostReason}
                        setLostReason={setLostReason}
                    />
                </ResponsiveModalBody>
                <ResponsiveModalFooter>
                    <StatusModalFooter
                        value={value}
                        mode={mode}
                        updateRoute={updateRoute}
                        resourceId={resourceId}
                        onChange={onChange}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        onClose={() => {
                            setIsOpen(false);
                            setLostReason(''); // Reset lost_reason when closing
                        }}
                        statusType={statusType}
                        lostReason={lostReason}
                    />
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
